// src/navigation/Navigation.tsx
import { lazy, Suspense, useMemo } from "react";
import { useSelector } from "react-redux";
import { Routes, Route, Navigate, Outlet } from "react-router-dom";

import { getBestAccessiblePath } from "./bestAccessibleRoute";
import { buildRoutesFromFeatures } from "./featureRouteGenerator";
import { ProtectedRoute } from "./ProtectedRoute";

import { Login } from "@/auth/login";
import { isGuardianUser } from "@/auth/navigationPersona";
import { usePermission } from "@/auth/permissions/usePermission";
import { DashboardLayout } from "@/components/layouts/DashboardLayout";
import { RouteLoading } from "@/components/RouteLoading";
import { WHATSAPP_AUTOMATION_PERMISSIONS } from "@/features/communication/whatsapp/permissions";
import { FEATURES } from "@/features/featureRegistry";
import { PlatformOwnerLayout } from "@/features/platformOwner/components/PlatformOwnerLayout";
import { PlatformOwnerRoute } from "@/features/platformOwner/components/PlatformOwnerRoute";
import { PlatformPlaceholderPage } from "@/features/platformOwner/components/PlatformPlaceholderPage";
import { PlatformSupportSessionWatcher } from "@/features/platformOwner/components/PlatformSupportSessionWatcher";
import { PLATFORM_PLACEHOLDER_ROUTES } from "@/features/platformOwner/navigation/platformPlaceholderRoutes";
import type { RootState } from "@/redux/store";
import { Forbidden } from "@/screens/errors/Forbidden";

const ReceiptPreviewPage = lazy(
  () => import("@/features/feesFinance/payments/pages/ReceiptPreviewPage"),
);
const FinancialReportPreviewPage = lazy(
  () => import("@/features/feesFinance/financeReports/print/FinancialReportPreviewPage"),
);
const PasswordSetupPage = lazy(() => import("@/auth/password/PasswordSetupPage"));

const LoginCallbackPage = lazy(() => import("@/auth/login/pages/LoginCallbackPage"));

const PublicAdmissionEnquiryPage = lazy(
  () => import("@/features/academics/admissions/pages/PublicAdmissionEnquiryPage"),
);
const NotificationInboxPage = lazy(
  () => import("@/features/communication/notifications/NotificationInboxPage"),
);
const WhatsAppMessagesPage = lazy(
  () => import("@/features/communication/whatsapp/pages/WhatsAppMessagesPage"),
);
const PlatformOverviewPage = lazy(
  () => import("@/features/platformOwner/tenants/pages/PlatformOverviewPage"),
);
const TenantsListPage = lazy(
  () => import("@/features/platformOwner/tenants/pages/TenantsListPage"),
);
const TenantDetailPage = lazy(
  () => import("@/features/platformOwner/tenants/pages/TenantDetailPage"),
);
const BulkImportPage = lazy(
  () => import("@/features/platformOwner/bulkImport/pages/BulkImportPage"),
);
const LegacyDataMigrationPage = lazy(
  () => import("@/features/platformOwner/legacyMigration/pages/LegacyDataMigrationPage"),
);
const MigrationManagementPage = lazy(
  () =>
    import("@/features/platformOwner/migrationManagement/pages/MigrationManagementPage"),
);
const WhatsAppOperationsPage = lazy(
  () => import("@/features/platformOwner/whatsapp/pages/WhatsAppOperationsPage"),
);
const MessagingOperationsPage = lazy(
  () => import("@/features/platformOwner/whatsapp/pages/MessagingOperationsPage"),
);

// A small wrapper that either renders children (protected) or redirects to /login
function RequireAuth({ isAuthenticated }: { isAuthenticated: boolean }) {
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  return <Outlet />;
}

// If user is logged in and visits /login, redirect to dashboard
function RedirectIfAuth({
  isAuthenticated,
  redirectTo,
}: {
  isAuthenticated: boolean;
  redirectTo: string;
}) {
  if (isAuthenticated) return <Navigate to={redirectTo} replace />;
  return <Outlet />;
}

function RedirectToBest({ to }: { to: string }) {
  return <Navigate to={to} replace />;
}

export default function Navigation() {
  const isAuthenticated = useSelector((state: RootState) => state.auth.isAuthenticated);
  const user = useSelector((state: RootState) => state.auth.user);

  const { can } = usePermission();

  const bestPath = useMemo(() => {
    // A platform-scope user (not currently acting inside a tenant) has no tenant
    // context/permissions at all — getBestAccessiblePath assumes tenant permission
    // keys and would otherwise dead-end at /403 for them.
    if (user?.scope === "PLATFORM" && !user?.isPlatformSupportSession) {
      return "/platform";
    }

    // Prefer dashboard when allowed; otherwise choose the highest-priority accessible feature.
    return getBestAccessiblePath(can, {
      preferredFeatureIds: ["dashboard"],
      fallbackPath: "/403",
      persona: isGuardianUser(user) ? "guardian" : "staff",
    });
  }, [can, user]);

  const safeBestPath =
    typeof bestPath === "string" && bestPath.trim() ? bestPath : "/403";

  return (
    <>
      <PlatformSupportSessionWatcher />
      <Routes>
        {/* Public routes */}
        <Route
          path="/public/enquiry/:tenantReference"
          element={
            <Suspense fallback={null}>
              <PublicAdmissionEnquiryPage />
            </Suspense>
          }
        />
        {/* Public, and deliberately outside RedirectIfAuth: redeeming a setup link revokes every
            session, so someone already signed in must still be able to open it. */}
        <Route path="/password/setup" element={<PasswordSetupPage />} />

        {/* Where Google's redirect chain lands. Public and outside RedirectIfAuth: the page's whole
            job is to exchange a code and establish a session, so it must run before auth exists. */}
        <Route path="/login/callback" element={<LoginCallbackPage />} />

        <Route
          element={
            <RedirectIfAuth isAuthenticated={isAuthenticated} redirectTo={safeBestPath} />
          }
        >
          <Route path="/login" element={<Login />} />
        </Route>

        {/* Protected app routes: all nested routes require auth */}
        <Route element={<RequireAuth isAuthenticated={isAuthenticated} />}>
          <Route path="403" element={<Forbidden />} />
          {/* Platform Owner area — gated on the platform_role claim / support-session state
              (PlatformOwnerRoute), not the tenant permission system, and not mounted through
              buildRoutesFromFeatures since it has no tenant context/sidebar. */}
          <Route
            path="platform"
            element={
              <PlatformOwnerRoute>
                <PlatformOwnerLayout />
              </PlatformOwnerRoute>
            }
          >
            <Route
              index
              element={
                <Suspense fallback={null}>
                  <PlatformOverviewPage />
                </Suspense>
              }
            />
            <Route
              path="tenants"
              element={
                <Suspense fallback={null}>
                  <TenantsListPage />
                </Suspense>
              }
            />
            <Route
              path="tenants/:tenantId"
              element={
                <Suspense fallback={null}>
                  <TenantDetailPage />
                </Suspense>
              }
            />
            <Route
              path="bulk-import"
              element={
                <Suspense fallback={null}>
                  <BulkImportPage />
                </Suspense>
              }
            />
            <Route
              path="legacy-migration"
              element={
                <Suspense fallback={null}>
                  <LegacyDataMigrationPage />
                </Suspense>
              }
            />
            <Route
              path="migrations"
              element={
                <Suspense fallback={null}>
                  <MigrationManagementPage />
                </Suspense>
              }
            />
            <Route
              path="whatsapp"
              element={
                <Suspense fallback={null}>
                  <WhatsAppOperationsPage />
                </Suspense>
              }
            />
            <Route
              path="messaging"
              element={
                <Suspense fallback={null}>
                  <MessagingOperationsPage />
                </Suspense>
              }
            />
            {/* Console sections whose backend does not exist yet. They route so the navigation
                reflects the intended shape of the console, but each states what has to be built
                rather than rendering a screen that looks functional. */}
            {PLATFORM_PLACEHOLDER_ROUTES.map((route) => (
              <Route
                key={route.path}
                path={route.path}
                element={
                  <PlatformPlaceholderPage
                    title={route.title}
                    description={route.description}
                    missingApi={route.missingApi}
                  />
                }
              />
            ))}
          </Route>
          <Route path="/" element={<DashboardLayout />}>
            <Route index element={<RedirectToBest to={safeBestPath} />} />
            <Route
              path="notifications"
              element={
                <Suspense fallback={<RouteLoading />}>
                  <NotificationInboxPage />
                </Suspense>
              }
            />
            <Route
              path="whatsapp/messages"
              element={
                <ProtectedRoute permission={WHATSAPP_AUTOMATION_PERMISSIONS.read}>
                  <Suspense fallback={<RouteLoading />}>
                    <WhatsAppMessagesPage />
                  </Suspense>
                </ProtectedRoute>
              }
            />
            <Route
              path="receipt-preview"
              element={
                <Suspense fallback={<RouteLoading />}>
                  <ReceiptPreviewPage />
                </Suspense>
              }
            />
            <Route
              path="financial-report-preview"
              element={
                <Suspense fallback={<RouteLoading />}>
                  <FinancialReportPreviewPage />
                </Suspense>
              }
            />
            {buildRoutesFromFeatures(FEATURES, {
              suspenseFallback: <RouteLoading />,
              persona: isGuardianUser(user) ? "guardian" : "staff",
            })}
          </Route>
        </Route>

        {/* Fallbacks */}
        <Route
          path="/"
          element={
            isAuthenticated ? (
              <RedirectToBest to={safeBestPath} />
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  );
}
